import React from "react";
import FlowchartSVG from "./widgetsAndUtils/FlowchartSVG";

export default function InstructionsTab() {
  return (
    <div className="lg:flex bg-gray-50" style={{ height: 'calc(100vh - 120px)' }}>
      {/* Main Content Area */}
      <div className="lg:flex-1 lg:overflow-y-auto bg-white">
        <div className="max-w-4xl mx-auto p-4 lg:p-8">
          <h1 className="text-3xl font-bold text-[#4679B2] mb-6">How to use this app</h1>

          <p className="text-base leading-relaxed text-gray-800 mb-6">
            To explore the performance of a BMP of interest, follow the steps illustrated in the flowchart outlining the main functions of the web page.
          </p>

          {/* Flowchart SVG */}
          <div className="flex flex-col items-center my-8 w-full">
            <FlowchartSVG />
            <span className="text-gray-600 text-sm text-center px-2 mt-4">
              Application workflow flowchart
            </span>
          </div>

          <p className="text-base leading-relaxed text-gray-800 mb-6">
            Follow the steps below to explore the web page:
          </p>

          <h2 className="text-2xl font-bold text-[#4679B2] mb-4">Step 1: Select a BMP or group of BMPs → go to BMP Selection Tab</h2>

          <p className="text-base leading-relaxed text-gray-800 mb-6">
            From the BMP Selection tab, you may choose to review data from a single BMP, or all the BMPs of a particular type. In all cases, options are limited to the availability of data stored in the SMC BMP Performance Monitoring Database maintained by SCCWRP. All BMPs and sites are anonymized. Multiple BMPs may be monitored at the same site (general physical location, like along a roadway segment).
          </p>

          <p className="text-base leading-relaxed text-gray-800 mb-4">
            <span className="font-semibold">Analysis Type = Individual BMPs.</span> Users can select a specific BMP of interest by site ID and BMP ID.
          </p>

          <p className="text-base leading-relaxed text-gray-800 mb-6">
            <span className="font-semibold">Analysis Type = BMP Type.</span> Users are currently limited to bioretention. Additional BMPs may become available as the SMC BMP Monitoring Network continues to expand and incorporate additional sites and BMPs.
          </p>

          <h2 className="text-2xl font-bold text-[#4679B2] mb-4">Step 2: Go to the data type of interest → go to Water Quality or Hydrology Tab</h2>

          <p className="text-base leading-relaxed text-gray-800 mb-6">
            Users can navigate to either Water Quality or Hydrology, depending on the type of data they wish to evaluate. The Multi-Parameter Index becomes available only after plots have been generated for either Water Quality or Hydrology.
          </p>

          <h2 className="text-2xl font-bold text-[#4679B2] mb-4">Step 3: Generate plots and view results</h2>

          <h3 className="text-xl font-bold text-[#4679B2] mb-4">Water Quality</h3>

          <ul className="list-disc ml-8 mb-6 space-y-2">
            <li className="text-base leading-relaxed text-gray-800">
              Select the analytes of interest by clicking [Add Analyte +].
            </li>
            <li className="text-base leading-relaxed text-gray-800">
              Click "Go" to generate the results.
            </li>
            <li className="text-base leading-relaxed text-gray-800">
              A default threshold is automatically applied for the Performance Index; users may adjust this threshold as needed.
            </li>
            <li className="text-base leading-relaxed text-gray-800">
              Analytes are limited to those for which data have been submitted. Each SMC agency conducting monitoring makes decisions on the analyte suite for their BMP(s).
            </li>
          </ul>

          <p className="text-base leading-relaxed text-gray-800 mb-4">
            To compare the performance of a single BMP against data from other BMPs of the same type:
          </p>

          <ul className="list-disc ml-8 mb-6 space-y-2">
            <li className="text-base leading-relaxed text-gray-800">
              BMP Selection = Individual BMP
            </li>
            <li className="text-base leading-relaxed text-gray-800">
              Water Quality tab: activate the slider for "Show Comparison Data" next to the "Go" button.
            </li>
          </ul>

          <h3 className="text-xl font-bold text-[#4679B2] mb-4">Hydrology</h3>

          <p className="text-base leading-relaxed text-gray-800 mb-6">
            Click "Go" directly to display hydrology results.
          </p>

          <h3 className="text-xl font-bold text-[#4679B2] mb-4">Multi-Parameter Index</h3>

          <ul className="list-disc ml-8 mb-6 space-y-2">
            <li className="text-base leading-relaxed text-gray-800">
              After clicking "Go" for either Water Quality or Hydrology, click "Go" to generate the MPI results.
            </li>
            <li className="text-base leading-relaxed text-gray-800">
              If no analyte is selected, the MPI will display hydrology-only performance.
            </li>
            <li className="text-base leading-relaxed text-gray-800">
              When analytes are selected in the Water Quality tab and results are generated, the MPI will automatically integrate both hydrology and water quality indices.
            </li>
          </ul>

          <p className="text-base leading-relaxed text-gray-800 mb-6">
            An optional toggle allows comparison of results from the selected BMP to all other BMPs of the same type within the dataset for each analysis type.
          </p>

          <h2 className="text-2xl font-bold text-[#4679B2] mb-4">Step 4: Download results → go to Data download</h2>

          <p className="text-base leading-relaxed text-gray-800 mb-6">
            The Data Download tab allows users to export all data selected during analysis (including specific water quality analytes) in a downloadable format for further analysis or reporting. The exported table provides results for each monitored event and distinguishes between data from the selected BMP and data from other BMPs available within the dataset.
          </p>
        </div>
      </div>
    </div>
  );
}
